import React, { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import PlatformIconGrid from '@/components/PlatformIconGrid';
import PlatformMultiSelect from '@/components/PlatformMultiSelect';
import { usePlatformFilterStyle } from '@/lib/utils/platformFilterStyle';
import CampaignSelectDashboard from '@/components/CampaignSelectDashboard';
import { SidebarFilterGroup } from '@/components/SidebarFilterGroup';
import { RecentSearchInput } from '@/components/RecentSearchInput';
import { SubscriberRangeInput } from './SubscriberRangeInput';
import CountryMultiSelect from '@/components/CountryMultiSelect';
import LanguageMultiSelect from '@/components/LanguageMultiSelect';
import { pushSearchHistory } from '@/lib/utils/searchHistory';
import { useI18nText } from '@/lib/utils/i18nText';
import { CampaignNavBox } from '@/components/CampaignNavBox';
import { getChannelTypeFilterKey } from '@/lib/utils/channelPlatformMeta';
import { useAppSelector } from '@/store/hooks';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { GgcCheckbox } from '@/components/ui/ggc/checkbox';
import type { CampaignItem } from '@/components/CampaignSelect';
import { ADVANCED_FILTER_ENABLED } from './AdvancedFilterModal';
import { VISIBLE_CHANNEL_TYPES, INITIAL_FILTER } from '../../constants';
import { getAllowedStatuses } from '../../utils';
import { CampaignStatsSidebar } from '../CampaignStatsSidebar';
import { CampaignStatusFilter } from './CampaignStatusFilter';
import { CandidateGroupsFilter } from './CandidateGroupsFilter';
import { loadOldTags, loadRecentlyUsedTags } from '@/lib/api/panel.api';
import { getCampaigns } from '@/lib/api/campaign.api';
import { getLabels } from '@/lib/api/labels.api';
import { buildTagKindMap, TagKindChip, type TagKind } from '../tagSuggestions';
import type { FilterState, Campaign, Influencer } from '../../types';
import { ChevronDown } from 'lucide-react';

/* ── 로컬 훅: boolean 필터 옵션 ── */
function useBoolOptions() {
  const { t } = useI18nText();
  const yna = useMemo(() => [
    { value: '', label: t('Any', '전체') },
    { value: 'yes', label: t('Yes', '예') },
    { value: 'no', label: t('No', '아니오') },
  ], [t]);
  const ya = useMemo(() => [
    { value: '', label: t('Any', '전체') },
    { value: 'yes', label: t('Yes', '예') },
  ], [t]);
  return { yna, ya };
}

const FilterGroup = SidebarFilterGroup;

/* ── Portal dropdown hook: positions dropdown below an input, escaping overflow:auto ── */

function usePortalDropdown(inputRef: React.RefObject<HTMLInputElement | null>) {
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [open, setOpen] = useState(false);

  const openDropdown = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX, width: rect.width });
    setOpen(true);
  }, [inputRef]);

  const closeDropdown = useCallback(() => setOpen(false), []);

  return { pos, open, openDropdown, closeDropdown };
}

/* ── Tag autocomplete input: comma-separated multi-tag with portal dropdown ── */

export function TagFilterInput({
  value, onChange, onKeyDown = () => {}, variant = 'sidebar', inputClassName, placeholder, rosterNames = [],
}: {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  variant?: 'sidebar' | 'popover';
  /** Override the `<input>` class to match the host page's own input styling
   *  (defaults to the sidebar filter's `filter-input`). */
  inputClassName?: string;
  /** Override the placeholder (defaults to the variant's usual copy). */
  placeholder?: string;
  /** Roster names, shown as a priority suggestion group above the recent/old tag group. */
  rosterNames?: string[];
}) {
  const { t } = useI18nText();
  const [oldTags, setOldTags] = useState<string[]>([]);
  const [recentTags, setRecentTags] = useState<string[]>([]);
  const [campaignKindMap, setCampaignKindMap] = useState<Map<string, 'campaign' | 'roster'>>(() => new Map());
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const { pos, open: dropdownOpen, openDropdown, closeDropdown } = usePortalDropdown(inputRef);

  const loadTags = useCallback(() => {
    if (tagsLoaded) return;
    setTagsLoaded(true);
    Promise.all([
      loadOldTags().catch(() => []),
      loadRecentlyUsedTags().catch(() => []),
      // Cached (same key as the dashboard's own load) — classifies each suggestion
      // as a campaign name, roster name (status === 'roster'), or plain tag.
      getCampaigns({}).catch(() => [] as Campaign[]),
    ]).then(([old, recent, campaigns]) => {
      setOldTags(old.map((s) => s.trim()).filter(Boolean));
      setRecentTags(recent.map((s) => s.trim()).filter(Boolean));
      setCampaignKindMap(buildTagKindMap(campaigns));
    });
  }, [tagsLoaded]);

  // Campaign/roster classification, with the `rosterNames` prop folded in so
  // roster suggestions are labeled even when not present in the campaign list.
  const kindMap = useMemo(() => {
    const m = new Map(campaignKindMap);
    for (const n of rosterNames) {
      const v = n.trim();
      if (v && !m.has(v)) m.set(v, 'roster');
    }
    return m;
  }, [campaignKindMap, rosterNames]);

  // Filter on the last comma-separated segment (matching original TagAutoComplete behavior)
  const lastSegment = value.split(/, ?/).pop()?.trim() ?? '';

  // Roster names are shown as a priority group above the recent/old tag group
  // (rosters get synced onto influencer tags on import — see autoSyncUserTags).
  const rosterOptions = useMemo(() => {
    if (!dropdownOpen) return [];
    const pool = Array.from(new Set(rosterNames.map((s) => s.trim()).filter(Boolean)));
    if (lastSegment) {
      return pool
        .filter((name) => name.toLowerCase().includes(lastSegment.toLowerCase()) && name !== lastSegment)
        .slice(0, 5);
    }
    return pool.slice(0, 5);
  }, [dropdownOpen, lastSegment, rosterNames]);

  const tagOptions = useMemo(() => {
    if (!dropdownOpen) return [];
    const rosterSet = new Set(rosterOptions);
    let base: string[];
    if (lastSegment) {
      base = oldTags.filter((tag) => tag.toLowerCase().includes(lastSegment.toLowerCase()) && tag !== lastSegment);
    } else {
      const extras = oldTags.filter((s) => !recentTags.includes(s));
      base = [...recentTags, ...extras];
    }
    // Skip tags already shown as a roster suggestion above.
    return base.filter((tag) => !rosterSet.has(tag)).slice(0, 7);
  }, [dropdownOpen, lastSegment, oldTags, recentTags, rosterOptions]);

  // Flat list backing keyboard nav/selection — roster group first, tag group after.
  const autocompleteOptions = useMemo(() => [...rosterOptions, ...tagOptions], [rosterOptions, tagOptions]);

  // Replace last segment with chosen tag, preserve leading segments
  const selectOption = (tag: string) => {
    const parts = value.split(/, ?/);
    parts[parts.length - 1] = tag;
    onChange(parts.join(', '));
    closeDropdown();
    setActiveIndex(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, autocompleteOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if ((e.key === 'Enter' || e.key === 'Tab') && activeIndex >= 0 && autocompleteOptions[activeIndex]) {
      e.preventDefault();
      selectOption(autocompleteOptions[activeIndex]);
    } else if (e.key === 'Escape') {
      closeDropdown();
      setActiveIndex(-1);
    } else {
      onKeyDown(e);
    }
  };

  const hasValue = !!value?.trim();

  const renderOption = (option: string, idx: number, kind: TagKind) => (
    <button
      key={option}
      type="button"
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
        idx === activeIndex ? 'bg-ggc-bg-surface text-ggc-cta-blue' : 'text-ggc-text-primary hover:bg-ggc-bg-surface'
      }`}
      onMouseDown={(e) => { e.preventDefault(); selectOption(option); }}
    >
      <TagKindChip kind={kind} />
      <span className="truncate">{option}</span>
    </button>
  );

  const hasBothGroups = rosterOptions.length > 0 && tagOptions.length > 0;

  const autocompleteDropdown = dropdownOpen && autocompleteOptions.length > 0 && pos
    ? createPortal(
        <div
          style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
          className="max-h-52 overflow-y-auto rounded-ggc-sm border border-ggc-border-gray-dark bg-white shadow-ggc-md"
        >
          {hasBothGroups && <div className="tag-autocomplete-section-label">{t('Rosters', '로스터')}</div>}
          {rosterOptions.map((option, idx) => renderOption(option, idx, 'roster'))}
          {hasBothGroups && <div className="tag-autocomplete-divider" />}
          {hasBothGroups && <div className="tag-autocomplete-section-label">{t('Tags', '태그')}</div>}
          {tagOptions.map((option, i) => renderOption(option, rosterOptions.length + i, kindMap.get(option) ?? 'recent'))}
        </div>,
        document.body,
      )
    : null;

  if (variant === 'popover') {
    return (
      <>
        <div className={`filter-input-wrap${hasValue ? ' filter-input-wrap--has-value' : ''}`}>
          <input
            ref={inputRef}
            className={inputClassName ?? 'filter-input'}
            value={value}
            onChange={(e) => { onChange(e.target.value); setActiveIndex(-1); openDropdown(); }}
            onFocus={() => { loadTags(); openDropdown(); }}
            onBlur={() => setTimeout(closeDropdown, 150)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder ?? t('Tags', '태그')}
            title={t('Tags', '태그')}
          />
          {hasValue && (
            <button
              type="button"
              className="filter-input-clear"
              onClick={() => { onChange(''); closeDropdown(); }}
              title={t('Clear', '지우기')}
            >
              <i className="fa fa-times-circle" />
            </button>
          )}
        </div>
        {autocompleteDropdown}
      </>
    );
  }

  return (
    <div className="filter-section filter-section--compact">
      <div className={`filter-input-wrap${hasValue ? ' filter-input-wrap--has-value' : ''}`}>
        <input
          ref={inputRef}
          className={inputClassName ?? 'filter-input'}
          value={value}
          onChange={(e) => { onChange(e.target.value); setActiveIndex(-1); openDropdown(); }}
          onFocus={() => { loadTags(); openDropdown(); }}
          onBlur={() => setTimeout(closeDropdown, 150)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? t('Enter tags', '태그를 입력하세요')}
          title={t('Tags', '태그')}
        />
        {hasValue && (
          <button
            type="button"
            className="filter-input-clear"
            onClick={() => { onChange(''); closeDropdown(); }}
            title={t('Clear', '지우기')}
          >
            <i className="fa fa-times-circle" />
          </button>
        )}
      </div>
      {autocompleteDropdown}
    </div>
  );
}

/* ── Categories filter: button that opens a modal multi-select ── */

const CATEGORIES_CHANNEL_TYPES = ['youtube', 'twitch', 'instagram', 'tiktok'];

export function CategoriesFilterInput({ value, onChange, variant = 'sidebar' }: {
  value: string;
  onChange: (patch: Partial<FilterState>) => void;
  variant?: 'sidebar' | 'popover';
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [allCategories, setAllCategories] = useState<{ title: string; channelTypes?: string[] }[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [activeChannelTypes, setActiveChannelTypes] = useState<string[]>(['youtube']);
  const [popStyle, setPopStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const loadCategories = useCallback(() => {
    if (loaded) return;
    setLoaded(true);
    setLoading(true);
    getLabels()
      .then((labels) => {
        const cats = labels
          // Drop labels with no title — a null title crashes the search filter.
          .filter((l) => l.type === 'category' && !!l.title)
          .map((l) => ({
            title: l.title,
            channelTypes: (l as { channelTypes?: string[] }).channelTypes,
          }));
        setAllCategories(cats);
      })
      // Loading failed → allow a later open to retry the fetch.
      .catch(() => { setLoaded(false); })
      .finally(() => setLoading(false));
  }, [loaded]);

  const selected = useMemo(
    () => value.split(',').map((s) => s.trim()).filter(Boolean),
    [value],
  );

  // Filter by the active channel-type tabs and the typed search query.
  const visibleCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allCategories.filter((cat) =>
      (!cat.channelTypes?.length || cat.channelTypes.some((ct) => activeChannelTypes.includes(ct))) &&
      (!q || (cat.title ?? '').toLowerCase().includes(q)),
    );
  }, [allCategories, activeChannelTypes, query]);

  const toggleCategory = (title: string) => {
    const next = selected.includes(title)
      ? selected.filter((s) => s !== title)
      : [...selected, title];
    onChange({ categories: next.join(', ') });
  };

  const toggleChannelType = (ct: string) => {
    setActiveChannelTypes((prev) =>
      prev.includes(ct) ? prev.filter((x) => x !== ct) : [...prev, ct],
    );
  };

  const hasValue = selected.length > 0;

  const openPopover = () => { loadCategories(); setOpen(true); };

  // Anchor the popover to the right of the trigger; fall back to the left and
  // clamp within the viewport when there isn't room.
  const position = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const W = 340;
    const GAP = 8;
    const MARGIN = 8;
    let left = rect.right + GAP;
    if (left + W > window.innerWidth - MARGIN) left = Math.max(MARGIN, rect.left - W - GAP);
    const popH = popRef.current?.offsetHeight ?? 420;
    let top = rect.top;
    if (top + popH > window.innerHeight - MARGIN) top = Math.max(MARGIN, window.innerHeight - popH - MARGIN);
    setPopStyle({ position: 'fixed', top, left, width: W, zIndex: 1060, visibility: 'visible' });
  }, []);

  useLayoutEffect(() => {
    if (!open) { setPopStyle({ visibility: 'hidden' }); return; }
    position();
  }, [open, position, visibleCategories.length, loading, activeChannelTypes]);

  useEffect(() => {
    if (!open) { setQuery(''); return; }
    const focusId = window.setTimeout(() => searchRef.current?.focus(), 0);
    const onScroll = () => position();
    const onDown = (e: MouseEvent) => {
      const n = e.target as Node;
      if (popRef.current?.contains(n) || triggerRef.current?.contains(n)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusId);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, position]);

  const categoryPopover = open ? createPortal(
    <div ref={popRef} className="category-popover" style={popStyle} role="dialog">
      {/* Indeterminate loading bar while categories are fetched. */}
      {loading && <div className="category-loading-track" aria-hidden />}
      <div className="flex items-center justify-between px-4 pb-2 pt-3">
        <span className="ggc-text-section-header">{t('Select categories', '카테고리 선택')}</span>
        <button
          type="button"
          className="text-ggc-disabled transition hover:text-ggc-text-primary"
          onClick={() => setOpen(false)}
          aria-label={t('Close', '닫기')}
        >
          <i className="fa fa-times" />
        </button>
      </div>

      {/* Type-to-filter search. */}
      <div className="px-4 pb-2">
        <div className={`filter-input-wrap${query ? ' filter-input-wrap--has-value' : ''}`}>
          <input
            ref={searchRef}
            type="text"
            className="filter-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search categories', '카테고리 검색')}
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="filter-input-clear"
              onClick={() => setQuery('')}
              title={t('Clear', '지우기')}
            >
              <i className="fa fa-times-circle" />
            </button>
          )}
        </div>
      </div>

      <div className="mb-2 flex flex-wrap gap-1.5 px-4">
        {CATEGORIES_CHANNEL_TYPES.map((ct) => (
          <button
            key={ct}
            type="button"
            onClick={() => toggleChannelType(ct)}
            className={`inline-flex items-center gap-1 rounded-ggc-pill border px-2 py-0.5 text-xs transition ${
              activeChannelTypes.includes(ct)
                ? 'border-ggc-cta-blue bg-white text-ggc-cta-blue'
                : 'border-ggc-border-gray-dark bg-white text-ggc-text-gray'
            }`}
          >
            <ChannelTypeIcon type={ct} size={12} />
            <span className="capitalize">{ct}</span>
          </button>
        ))}
      </div>

      <div className="max-h-72 overflow-y-auto px-4 pb-3">
        {loading ? (
          <div className="py-8 text-center text-xs text-ggc-disabled">{t('Loading categories...', '카테고리 불러오는 중...')}</div>
        ) : visibleCategories.length === 0 ? (
          <div className="py-8 text-center text-xs text-ggc-disabled">
            {query ? t('No matching categories', '검색 결과가 없습니다') : t('No categories', '카테고리 없음')}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {visibleCategories.map((cat) => {
              const isSelected = selected.includes(cat.title);
              return (
                <button
                  key={cat.title}
                  type="button"
                  onClick={() => toggleCategory(cat.title)}
                  className={`inline-flex h-[28px] items-center gap-1 rounded-ggc-sm border px-2 py-0.5 text-xs transition ${
                    isSelected
                      ? 'border-ggc-green bg-ggc-green-light text-ggc-green'
                      : 'border-ggc-border-gray-dark bg-white text-ggc-text-gray hover:border-ggc-cta-blue'
                  }`}
                >
                  {isSelected && <i className="fa fa-check" style={{ fontSize: 9 }} />}
                  {cat.title}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-ggc-border-gray px-4 py-2.5">
        <span className="text-xs text-ggc-text-gray">
          {selected.length > 0 ? t(`${selected.length} selected`, `${selected.length}개 선택됨`) : ''}
        </span>
        <div className="flex gap-2">
          {selected.length > 0 && (
            <button type="button" className="text-xs text-ggc-disabled hover:text-ggc-text-gray"
              onClick={() => onChange({ categories: '' })}
            >
              {t('Clear all', '전체 해제')}
            </button>
          )}
          <button
            type="button"
            className="rounded-ggc-sm bg-ggc-cta-blue px-4 py-1.5 text-xs text-white hover:opacity-90"
            onClick={() => setOpen(false)}
          >
            {t('Done', '완료')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  const triggerButton = (placeholder: string) => (
    <div ref={triggerRef} className={`filter-input-wrap${hasValue ? ' filter-input-wrap--has-value' : ''}`}>
      <button
        type="button"
        className="filter-input text-left truncate"
        style={{ cursor: 'pointer', background: 'white' }}
        onClick={openPopover}
        title={t('Categories', '카테고리')}
      >
        {hasValue
          ? <span className="text-black">{selected.join(', ')}</span>
          : <span className="select-trigger__text--empty flex items-center justify-between w-full">
              {placeholder}
              <ChevronDown
                className={`ml-auto h-4 w-4 shrink-0 text-ggc-text-gray-light transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </span>
        }
      </button>
      {hasValue && (
        <button
          type="button"
          className="filter-input-clear"
          onClick={() => onChange({ categories: '' })}
          title={t('Clear', '지우기')}
        >
          <i className="fa fa-times-circle" />
        </button>
      )}
    </div>
  );

  if (variant === 'popover') {
    return (
      <>
        {triggerButton(t('Categories', '카테고리'))}
        {categoryPopover}
      </>
    );
  }

  return (
    <div className="filter-section filter-section--compact">
      {triggerButton(t('Select categories', '카테고리를 선택하세요'))}
      {categoryPopover}
    </div>
  );
}

function FilterToggle({ label, value, field, options, onChange }: {
  label: string; value: string; field: string;
  options: { value: string; label: string }[];
  onChange: (p: Partial<FilterState>) => void;
}) {
  return (
    <div className="filter-toggle-row">
      <span className="filter-toggle-label">{label}</span>
      <div className="filter-toggle-btns">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`filter-toggle-btn${value === o.value ? ' filter-toggle-btn--active' : ''}${value === o.value && o.value === '' ? ' filter-toggle-btn--default' : ''}`}
            onClick={() => onChange({ [field]: o.value } as Partial<FilterState>)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ──────────────────── 채널 필터 사이드바 ──────────────────── */

// Text search fields that keep a recent-search history (datalist). `country`/`language`
// are checkbox multi-selects (CountryMultiSelect/LanguageMultiSelect), not free-text
// fields, so they have no history.
const SEARCH_HISTORY_FIELDS = [
  'channelName', 'recentGame', 'email',
  'subscriberCount', 'recentGenre', 'keywords', 'inclusionComment',
] as const satisfies readonly (keyof FilterState)[];

const HAS_ANY_SKIP_KEYS = new Set(['page', 'status', 'filterByCampaignId', 'candidateGroupName']);

/** Is any search-condition/channel-type filter currently active? Shared by the expanded
 *  ChannelFilterSidebar and the collapsed sidebar's search popover so their Apply/Clear
 *  buttons agree on when there's something to apply/clear. */
export function computeHasAnyFilter(filter: FilterState): boolean {
  return (Object.keys(INITIAL_FILTER) as (keyof typeof INITIAL_FILTER)[]).some((k) => {
    if (HAS_ANY_SKIP_KEYS.has(k)) return false;
    const v = filter[k];
    // Array-valued filters (e.g. country) can't be compared with !== against
    // INITIAL_FILTER's array — that's always a different reference.
    return Array.isArray(v) ? v.length > 0 : v !== INITIAL_FILTER[k];
  });
}

/**
 * Search-condition fields shared by the expanded ChannelFilterSidebar and the collapsed
 * sidebar's search popover (see influencers/index.tsx's CollapsedSelectSlot). These used to be
 * two hand-duplicated field lists — the collapsed one had fallen out of sync (plain `<input>`s
 * instead of RecentSearchInput/SubscriberRangeInput, no Categories field styling parity) and
 * looked different from the expanded sidebar. Both now render from this one component so they
 * stay visually and behaviorally identical. `onApply` is called on Enter in any field — this
 * also records each field into its recent-search history first, matching the original per-field
 * Enter behavior.
 */
export function SearchConditionFields({
  filter,
  onChange,
  onApply,
  isAdmin,
}: {
  filter: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
  onApply: () => void;
  isAdmin: boolean;
}) {
  const { t } = useI18nText();
  const { ya: BOOL_OPTIONS_YA } = useBoolOptions();

  const applyWithHistory = () => {
    SEARCH_HISTORY_FIELDS.forEach((f) => pushSearchHistory(f, String(filter[f] ?? '')));
    onApply();
  };
  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') applyWithHistory(); };

  return (
    <>
      {/* Each input shows a recent-search dropdown with per-item delete (see RecentSearchInput). */}
      <RecentSearchInput value={filter.channelName} onChange={(v) => onChange({ channelName: v })} onKeyDown={handleKeyDown} historyKey="channelName" placeholder={t('Enter channel name', '채널명을 입력하세요')} />
      <div className="filter-section--compact">
        <CountryMultiSelect value={filter.country} onChange={(vals) => onChange({ country: vals })} placeholder={t('Select country', '국가 선택')} portalled />
      </div>
      <div className="filter-section--compact">
        <LanguageMultiSelect value={filter.language} onChange={(vals) => onChange({ language: vals })} placeholder={t('Select language', '언어 선택')} portalled />
      </div>
      <RecentSearchInput value={filter.recentGame} onChange={(v) => onChange({ recentGame: v })} onKeyDown={handleKeyDown} historyKey="recentGame" placeholder={t('Enter recent game', '최근 게임을 입력하세요')} />
      {isAdmin && <TagFilterInput value={filter.tags} onChange={(v) => onChange({ tags: v })} onKeyDown={handleKeyDown} />}
      {isAdmin && <RecentSearchInput value={filter.email} onChange={(v) => onChange({ email: v })} onKeyDown={handleKeyDown} historyKey="email" placeholder={t('Enter e-mail', '이메일을 입력하세요')} />}
      <SubscriberRangeInput value={filter.subscriberCount} onChange={(v) => onChange({ subscriberCount: v })} placeholder={t('Enter subscribers', '구독자를 입력하세요')} />
      {isAdmin && <CategoriesFilterInput value={filter.categories} onChange={onChange} />}
      {isAdmin && <RecentSearchInput value={filter.recentGenre} onChange={(v) => onChange({ recentGenre: v })} onKeyDown={handleKeyDown} historyKey="recentGenre" placeholder={t('Enter recent genre', '최근 장르를 입력하세요')} />}
      <RecentSearchInput value={filter.keywords} onChange={(v) => onChange({ keywords: v })} onKeyDown={handleKeyDown} historyKey="keywords" placeholder={t('Enter keywords', '키워드를 입력하세요')} />
      {isAdmin && <RecentSearchInput value={filter.inclusionComment} onChange={(v) => onChange({ inclusionComment: v })} onKeyDown={handleKeyDown} historyKey="inclusionComment" placeholder={t('Enter inc. comment', 'inc. comment를 입력하세요')} />}
      <FilterToggle label={t('Campaign history', '캠페인 참여 이력')} value={filter.hasCampaignHistory} field="hasCampaignHistory" options={BOOL_OPTIONS_YA} onChange={onChange} />
    </>
  );
}

/** Apply/Clear footer shared by the expanded and collapsed search-condition panels. */
export function SearchConditionApplyRow({
  hasAny,
  onApply,
  onClear,
  onApplied,
}: {
  hasAny: boolean;
  onApply: () => void;
  onClear: () => void;
  /** Fired only when the Apply BUTTON itself is clicked — never from an auto-apply/debounce
   *  path that also calls `onApply` (e.g. ChannelFilterSidebar's channelName auto-apply).
   *  Used to re-collapse a sidebar that was temporarily expanded just to search. */
  onApplied?: () => void;
}) {
  const { t } = useI18nText();
  return (
    <div className="pt-2 flex gap-1">
      <button
        type="button"
        onClick={() => { onApply(); onApplied?.(); }}
        disabled={!hasAny}
        className="flex-1 rounded-ggc-sm bg-[var(--primary-600)] py-1.5 text-xs font-medium text-white hover:bg-[var(--primary-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[var(--primary-600)]"
      >
        <i className="fa fa-search mr-1" />
        {t('Apply', '적용')}
      </button>
      {hasAny && (
        <button
          type="button"
          onClick={onClear}
          className="rounded-ggc-sm border border-ggc-border-gray px-2 text-xs text-ggc-disabled hover:text-ggc-red hover:border-ggc-red transition-colors"
          title={t('Clear all', '모두 초기화')}
        >
          <i className="fa fa-times" />
        </button>
      )}
    </div>
  );
}

export function ChannelFilterSidebar({
  filter, onChange, onApply, onClear, selectedCampaignLabel,
  campaigns, campaignsLoading, onSelectCampaign, selectedCampaignId,
  onOpenAdvancedFilter, isAdmin,
  isCampaignMode, influencerStatuses, onStatusClick, campaignAnalysis,
  selectedCampaign, candidateGroups, candidateGroupsLoading,
  onCandidateGroupClick,
  onCreateCandidateGroup, onAddToCandidateGroup, onRemoveFromCandidateGroup,
  onRemoveCandidateGroup, onCandidateGroupsCommitted,
  selectedUserIds, influencers, onCampaignFilterChange,
  clientHasScreenshot, onChangeClientHasScreenshot,
  searchOnly = false,
  onApplied,
}: {
  filter: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
  onApply: () => void;
  onClear: () => void;
  selectedCampaignLabel: string;
  campaigns: CampaignItem[];
  campaignsLoading: boolean;
  onSelectCampaign: (id: string) => void;
  selectedCampaignId: string;
  onOpenAdvancedFilter: () => void;
  isAdmin: boolean;
  isCampaignMode: boolean;
  influencerStatuses: { byIdTotal?: Record<string, number>; byIdUnread?: Record<string, number> } | null;
  onStatusClick: (status: string) => void;
  campaignAnalysis: { total?: { youtube?: { views?: number; likes?: number; comments?: number }; clicks?: { web?: number; ios?: number; android?: number; defaultURL?: number } } } | null;
  selectedCampaign: Campaign | null;
  candidateGroups: Record<string, number>;
  candidateGroupsLoading: boolean;
  onCandidateGroupClick: (groupName: string) => void;
  onCreateCandidateGroup: (userIds: string[], groupName: string) => void;
  onAddToCandidateGroup: (userIds: string[], groupName: string) => void;
  onRemoveFromCandidateGroup: (userIds: string[]) => void;
  onRemoveCandidateGroup: (groupName: string) => void;
  onCandidateGroupsCommitted: (renames: Array<{ from: string; to: string }>) => void;
  selectedUserIds: Set<string>;
  influencers: Influencer[];
  onCampaignFilterChange: (id: string) => void;
  clientHasScreenshot: boolean;
  onChangeClientHasScreenshot: (v: boolean) => void;
  /** Search-and-add drawer mode: hide the campaign-membership filter dropdown, keep only search conditions. */
  searchOnly?: boolean;
  /** Fired when the Apply button is clicked (not on auto-apply) — see SearchConditionApplyRow. */
  onApplied?: () => void;
}) {
  const { t } = useI18nText();
  const { ya: BOOL_OPTIONS_YA, yna: BOOL_OPTIONS_YNA } = useBoolOptions();
  const currentUser = useAppSelector((s) => s.envs.currentUser as { email?: string } | null);
  const isDevTester = currentUser?.email === 'daeho@ggcontent.com';
  const [devDialogOpen, setDevDialogOpen] = useState(false);

  // Record each search field's current value into its recent-search history (shown in the
  // RecentSearchInput dropdowns), then apply. The bump forces those dropdowns to re-read.
  const [, bumpHistory] = useState(0);
  const applyWithHistory = () => {
    SEARCH_HISTORY_FIELDS.forEach((f) => pushSearchHistory(f, String(filter[f] ?? '')));
    bumpHistory((v) => v + 1);
    onApply();
  };

  // Channel-name field: auto-apply 1s after typing stops (debounced search), like pressing Apply.
  const applyRef = useRef(applyWithHistory);
  applyRef.current = applyWithHistory;
  const autoApplyInitRef = useRef(true);
  const lastAutoAppliedName = useRef(filter.channelName);
  useEffect(() => {
    if (autoApplyInitRef.current) {
      autoApplyInitRef.current = false;
      lastAutoAppliedName.current = filter.channelName;
      return;
    }
    if (filter.channelName === lastAutoAppliedName.current) return;
    const id = setTimeout(() => {
      lastAutoAppliedName.current = filter.channelName;
      applyRef.current();
    }, 1000);
    return () => clearTimeout(id);
  }, [filter.channelName]);

  const hasAny = useMemo(() => computeHasAnyFilter(filter), [filter]);

  const toggleChannelType = (type: string) => {
    const key = getChannelTypeFilterKey(type) as keyof FilterState | undefined;
    if (!key) return;
    onChange({ [key]: !filter[key] } as Partial<FilterState>);
  };

  const allChannelTypesActive = VISIBLE_CHANNEL_TYPES.every((type) => {
    const key = getChannelTypeFilterKey(type) as keyof FilterState | undefined;
    return key ? !!filter[key] : true;
  });
  const toggleAllChannelTypes = () => {
    const next = !allChannelTypesActive;
    const patch: Partial<FilterState> = {};
    VISIBLE_CHANNEL_TYPES.forEach((type) => {
      const key = getChannelTypeFilterKey(type) as keyof FilterState | undefined;
      if (key) (patch as Record<string, boolean>)[key] = next;
    });
    onChange(patch);
  };

  // PlatformMultiSelect's onChange is "the new set of active types" (MultiSelect's
  // usual value/onChange shape) rather than PlatformIconGrid's per-type toggle —
  // this adapts it onto the same `filter[key]` booleans.
  const activeChannelTypes = VISIBLE_CHANNEL_TYPES.filter((type) => {
    const key = getChannelTypeFilterKey(type) as keyof FilterState | undefined;
    return key ? !!filter[key] : true;
  });
  const setActiveChannelTypes = (nextActive: string[]) => {
    const patch: Partial<FilterState> = {};
    VISIBLE_CHANNEL_TYPES.forEach((type) => {
      const key = getChannelTypeFilterKey(type) as keyof FilterState | undefined;
      if (key) (patch as Record<string, boolean>)[key] = nextActive.includes(type);
    });
    onChange(patch);
  };
  const platformFilterStyle = usePlatformFilterStyle();

  return (
    <div className="dashboard-sidebar">
      {/* 캠페인 모드에서만 캠페인 선택 드롭다운 표시 */}
      {isCampaignMode && (
        <CampaignSelectDashboard
          campaigns={campaigns}
          campaignsLoading={campaignsLoading}
          selectedCampaignId={selectedCampaignId}
          selectedCampaignLabel={selectedCampaignLabel}
          onSelectCampaign={onSelectCampaign}
        />
      )}

      {/* 캠페인 모드: 메뉴 네비게이션 */}
      {isCampaignMode && selectedCampaignId && selectedCampaignId !== 'all' && (
        <CampaignNavBox campaignId={selectedCampaignId} campaignStatus={selectedCampaign?.status} />
      )}

      {/* 캠페인 모드: 인플루언서 상태 필터 */}
      {isCampaignMode && influencerStatuses && (
        <CampaignStatusFilter
          statuses={getAllowedStatuses(isAdmin)}
          influencerStatuses={influencerStatuses}
          selectedStatuses={filter.status}
          onStatusClick={onStatusClick}
          t={t}
        />
      )}

      {/* 캠페인 모드: Candidate groups */}
      {isCampaignMode && (
        <CandidateGroupsFilter
          campaign={selectedCampaign}
          candidateGroups={candidateGroups}
          candidateGroupsLoading={candidateGroupsLoading}
          currentGroupName={filter.candidateGroupName}
          onGroupClick={onCandidateGroupClick}
          onCreateGroup={onCreateCandidateGroup}
          onAddToGroup={onAddToCandidateGroup}
          onRemoveFromGroup={onRemoveFromCandidateGroup}
          onRemoveGroup={onRemoveCandidateGroup}
          onGroupsCommitted={onCandidateGroupsCommitted}
          selectedUserIds={selectedUserIds}
          influencers={influencers}
          selectedCampaignId={selectedCampaignId}
          t={t}
        />
      )}

      {/* 캠페인 모드: 캠페인 통계 */}
      {isCampaignMode && campaignAnalysis && (
        <CampaignStatsSidebar analysis={campaignAnalysis} />
      )}

      {/* 채널 필터 / 검색 옵션: 캠페인 모드에서는 숨김 */}
      {!isCampaignMode && (
        <>
          {/* 캠페인 소속 채널 필터 (검색 전용 드로어에서는 숨김) */}
          {!searchOnly && (
            <CampaignSelectDashboard
              campaigns={campaigns}
              campaignsLoading={campaignsLoading}
              selectedCampaignId={filter.filterByCampaignId}
              selectedCampaignLabel={campaigns.find((c) => c._id === filter.filterByCampaignId)?.name ?? ''}
              onSelectCampaign={onCampaignFilterChange}
              onClearFilter={() => onCampaignFilterChange('')}
              placeholder={t('All campaigns', '캠페인 필터링 없음')}
            />
          )}

          <FilterGroup title={t('Social Platform', '소셜 플랫폼')} defaultOpen>
            {platformFilterStyle === 'dropdown' ? (
              <PlatformMultiSelect
                types={VISIBLE_CHANNEL_TYPES}
                value={activeChannelTypes}
                onChange={setActiveChannelTypes}
                placeholder={t('All platforms', '전체 플랫폼')}
                portalled
              />
            ) : (
              <PlatformIconGrid
                types={VISIBLE_CHANNEL_TYPES}
                isActive={(type) => {
                  const key = getChannelTypeFilterKey(type) as keyof FilterState | undefined;
                  return key ? !!filter[key] : true;
                }}
                onToggle={toggleChannelType}
                onToggleAll={toggleAllChannelTypes}
                allActive={allChannelTypesActive}
                iconSize={14}
                toggleAllLabel={(all) => all ? t('Deselect all', '전체 해제') : t('Select all', '전체 선택')}
              />
            )}
          </FilterGroup>

          {/* ── 필터 그룹 ── */}
          <FilterGroup
            title={t('Search Conditions', '검색 조건')}
            defaultOpen
            action={ADVANCED_FILTER_ENABLED && isAdmin ? (
              <button className="filter-advanced-btn" onClick={onOpenAdvancedFilter}>
                <i className="fa fa-filter text-[8px]" />
                {t('Advanced', 'Advanced')}
              </button>
            ) : undefined}
          >
            <SearchConditionFields filter={filter} onChange={onChange} onApply={onApply} isAdmin={isAdmin} />
            {isDevTester && (
              <div className="filter-toggle-row" style={{ marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => setDevDialogOpen(true)}
                  className="w-full rounded-ggc-sm border border-dashed border-ggc-border-gray px-2 py-1 text-[11px] text-ggc-disabled hover:text-ggc-text-primary hover:border-ggc-text-primary transition-colors"
                  title={t('Dev-only: extra filter options', '개발 테스트용 추가 필터 옵션')}
                >
                  <i className="fa fa-flask mr-1" />
                  {t('Dev: More options', 'Dev: 추가 옵션')}
                </button>
              </div>
            )}
            <SearchConditionApplyRow hasAny={hasAny} onApply={applyWithHistory} onClear={onClear} onApplied={onApplied} />
          </FilterGroup>

          {isDevTester && (
            <Dialog open={devDialogOpen} onOpenChange={setDevDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('Dev: Extra filter options', 'Dev: 추가 필터 옵션')}</DialogTitle>
                  <DialogDescription>
                    {t(
                      'Developer-only filters for testing. Visible to daeho@ggcontent.com.',
                      '개발 테스트용 필터입니다. daeho@ggcontent.com 계정에서만 보입니다.',
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-1 py-2">
                  <FilterToggle label={t('Registered', '등록됨')} value={filter.isRegistered} field="isRegistered" options={BOOL_OPTIONS_YNA} onChange={onChange} />
                  <FilterToggle label={t('Has rates', '광고 단가')} value={filter.hasRates} field="hasRates" options={BOOL_OPTIONS_YNA} onChange={onChange} />
                  <FilterToggle label={t('Demographic', '데모그라픽')} value={filter.hasDemographic} field="hasDemographic" options={BOOL_OPTIONS_YNA} onChange={onChange} />
                  <FilterToggle label={t('Streams', '스트림')} value={filter.hasStreams} field="hasStreams" options={BOOL_OPTIONS_YA} onChange={onChange} />
                  <FilterToggle label={t('YT Streams', 'YT 스트림')} value={filter.youtubeWatchLiveStreams} field="youtubeWatchLiveStreams" options={BOOL_OPTIONS_YA} onChange={onChange} />
                  <FilterToggle label={t('TikTok Social', 'TikTok 소셜')} value={filter.updateTiktokSocialStats} field="updateTiktokSocialStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
                  <FilterToggle label={t('TikTok Channel', 'TikTok 채널')} value={filter.updateTiktokChannelStats} field="updateTiktokChannelStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
                  <FilterToggle label={t('Douyin Social', 'Douyin 소셜')} value={filter.updateDouyinSocialStats} field="updateDouyinSocialStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
                  <FilterToggle label={t('Douyin Channel', 'Douyin 채널')} value={filter.updateDouyinChannelStats} field="updateDouyinChannelStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
                  <FilterToggle label={t('FB Channel', 'FB 채널')} value={filter.updateFacebookChannelStats} field="updateFacebookChannelStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
                </div>

                <div className="mt-2 border-t border-ggc-border-gray pt-3">
                  <div className="mb-2 text-[11px] text-ggc-disabled">
                    <i className="fa fa-flask mr-1" />
                    {t('Client-side filters (current page only)', '클라이언트 필터 (현재 페이지만)')}
                  </div>
                  <GgcCheckbox
                    className="hover:text-ggc-text-primary"
                    checked={clientHasScreenshot}
                    onChange={(e) => onChangeClientHasScreenshot(e.target.checked)}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <i className="fa fa-camera text-ggc-text-secondary" />
                      {t('Only channels with demographic screenshots', '데모그라픽 스크린샷 보유 채널만')}
                    </span>
                  </GgcCheckbox>
                  <div className="mt-1 ml-6 text-[10px] text-ggc-disabled">
                    {t(
                      'Filters loaded rows only — does not refetch from server.',
                      '현재 페이지에 로드된 행만 필터링합니다 — 서버에서 다시 조회하지 않습니다.',
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* {isAdmin && ( */}
          {/* <FilterGroup title={t('Data Options', '기타 옵션')}> */}
          {/* <FilterToggle label={t('Registered', '등록됨')} value={filter.isRegistered} field="isRegistered" options={BOOL_OPTIONS_YNA} onChange={onChange} /> */}
          {/* <FilterToggle label={t('Has rates', '광고 단가')} value={filter.hasRates} field="hasRates" options={BOOL_OPTIONS_YNA} onChange={onChange} /> */}
          {/* <FilterToggle label={t('Demographic', '데모그라픽')} value={filter.hasDemographic} field="hasDemographic" options={BOOL_OPTIONS_YNA} onChange={onChange} /> */}
          {/* <FilterToggle label={t('Campaign history', '캠페인 참여 이력')} value={filter.hasCampaignHistory} field="hasCampaignHistory" options={BOOL_OPTIONS_YA} onChange={onChange} /> */}
          {/* <FilterToggle label={t('Streams', '스트림')} value={filter.hasStreams} field="hasStreams" options={BOOL_OPTIONS_YA} onChange={onChange} /> */}
          {/* <FilterToggle label={t('YT Streams', 'YT 스트림')} value={filter.youtubeWatchLiveStreams} field="youtubeWatchLiveStreams" options={BOOL_OPTIONS_YA} onChange={onChange} /> */}
          {/* <FilterToggle label={t('TikTok Social', 'TikTok 소셜')} value={filter.updateTiktokSocialStats} field="updateTiktokSocialStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
              <FilterToggle label={t('TikTok Channel', 'TikTok 채널')} value={filter.updateTiktokChannelStats} field="updateTiktokChannelStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
              <FilterToggle label={t('Douyin Social', 'Douyin 소셜')} value={filter.updateDouyinSocialStats} field="updateDouyinSocialStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
              <FilterToggle label={t('Douyin Channel', 'Douyin 채널')} value={filter.updateDouyinChannelStats} field="updateDouyinChannelStats" options={BOOL_OPTIONS_YA} onChange={onChange} />
              <FilterToggle label={t('FB Channel', 'FB 채널')} value={filter.updateFacebookChannelStats} field="updateFacebookChannelStats" options={BOOL_OPTIONS_YA} onChange={onChange} /> */}
          {/* </FilterGroup> */}
          {/* )} */}
        </>
      )}
    </div>
  );
}
